import { useState, useEffect, useContext } from "react";
import { useSearchParams } from "react-router-dom";
import { db } from "../firebase";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { QRCodeSVG } from "qrcode.react";
import { AuthContext } from "../AuthContext";
import { useTranslation } from "../i18n/LanguageContext";
import { canViewQrCode, getRoleLabel, normalizeRole } from "../utils/roleUtils";
import { isFollowableProfile, toggleFollowProfile, getFollowerCount } from "../utils/follows";
import ImageUploadField from "../components/ImageUploadField";
import Avatar from "../components/Avatar";
import AvatarEditor from "../components/AvatarEditor";
import { DEFAULT_AVATAR_CONFIG } from "../components/AvatarSVG";

function Profile() {
  const { user: currentUser } = useContext(AuthContext);
  const [searchParams] = useSearchParams();
  const [profile, setProfile] = useState(null);
  const [isOwnProfile, setIsOwnProfile] = useState(false);
  const [musicStyle, setMusicStyle] = useState("");
  const [city, setCity] = useState("");
  const [photoUrl, setPhotoUrl] = useState("");
  const [photoPath, setPhotoPath] = useState("");
  const [message, setMessage] = useState("");
  const [photoError, setPhotoError] = useState("");
  const [isFollowing, setIsFollowing] = useState(false);
  const [followerCount, setFollowerCount] = useState(0);
  const [avatarConfig, setAvatarConfig] = useState(DEFAULT_AVATAR_CONFIG);
  const [showAvatarEditor, setShowAvatarEditor] = useState(false);
  const { language, t } = useTranslation();

  useEffect(() => {
    const loadProfile = async () => {
      if (!currentUser) return;

      const uidFromUrl = searchParams.get("uid");
      const profileId = uidFromUrl || currentUser.uid;
      const docRef = doc(db, "users", profileId);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const data = docSnap.data();
        const nextProfile = { id: profileId, ...data };
        setProfile(nextProfile);
        setIsOwnProfile(profileId === currentUser.uid);
        setMusicStyle(data.musicStyle || "");
        setCity(data.city || "");
        setPhotoUrl(data.photoUrl || "");
        setPhotoPath(data.photoPath || "");
        setAvatarConfig(data.avatarConfig || DEFAULT_AVATAR_CONFIG);
        setFollowerCount(getFollowerCount(nextProfile));
      }

      const currentUserRef = doc(db, "users", currentUser.uid);
      const currentUserSnap = await getDoc(currentUserRef);
      const currentUserData = currentUserSnap.exists() ? currentUserSnap.data() : {};
      setIsFollowing((currentUserData.following || []).includes(profileId));

      if (profileId !== currentUser.uid) {
        const currentFriends = currentUserData.friends || [];
        if (!currentFriends.includes(profileId)) {
          setProfile((prev) => ({ ...prev, isBuddy: false }));
        }
      }
    };

    loadProfile();
  }, [currentUser, searchParams]);

  const handleFollowToggle = async () => {
    if (!currentUser || !profile || isOwnProfile) return;

    const nextFollowing = await toggleFollowProfile(currentUser.uid, profile.id);
    setIsFollowing(nextFollowing);
    setFollowerCount((prev) => (nextFollowing ? prev + 1 : Math.max(0, prev - 1)));
    setProfile((prev) => ({
      ...prev,
      followers: nextFollowing
        ? [...new Set([...(prev.followers || []), currentUser.uid])]
        : (prev.followers || []).filter((id) => id !== currentUser.uid),
    }));
  };

  // Storage upload/validation/replace/remove is fully owned by
  // ImageUploadField — this just receives the resulting {url, path}.
  const handlePhotoChange = ({ url, path }) => {
    setPhotoUrl(url);
    setPhotoPath(path);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!currentUser) return;

    setPhotoError("");
    const updatedData = {
      ...profile,
      musicStyle,
      city,
      photoUrl,
      photoPath,
      avatarConfig,
    };

    try {
      await setDoc(doc(db, "users", currentUser.uid), updatedData, { merge: true });
      setProfile((prev) => ({ ...prev, ...updatedData }));

      setMessage(`✅ ${t('profile.saved', 'Profile saved successfully!')}`);
      setTimeout(() => setMessage(""), 3000);
    } catch (err) {
      setPhotoError(t('profile.saveError', 'Could not save your profile. Please try again.'));
    }
  };

  if (!profile) {
    return <p className="text-center mt-20 text-white">{t('profile.loading', 'Loading profile...')}</p>;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-950 to-indigo-900 flex justify-center items-center px-6 py-10">
      <form
        onSubmit={handleSave}
        className="bg-slate-800/90 backdrop-blur-sm p-8 rounded-2xl shadow-xl w-full max-w-md border border-slate-700/50"
      >
        <h2 className="text-3xl font-bold mb-6 text-center text-white">
          {profile.username || t("profile.defaultTitle", "Profile")}
        </h2>
        <p className="text-center text-sky-200 text-sm mb-4">{getRoleLabel(normalizeRole(profile.role), language)}</p>
        {isFollowableProfile(profile) && !isOwnProfile && (
          <div className="mb-4 rounded-2xl border border-slate-700 bg-slate-900/70 p-3 text-center text-sm text-slate-100">
            <div className="text-xs uppercase tracking-[0.28em] text-sky-300/90">{t('profile.followers', 'Followers')}</div>
            <div className="mt-1 text-2xl font-semibold text-white">{followerCount}</div>
            {isFollowing ? (
              <div className="mt-3 flex flex-col gap-2">
                <button
                  type="button"
                  disabled
                  className="w-full cursor-default rounded-xl bg-emerald-600/90 px-4 py-2 text-sm font-semibold text-white"
                >
                  {t('profile.following', 'Following')}
                </button>
                <button
                  type="button"
                  onClick={handleFollowToggle}
                  className="w-full rounded-xl border border-slate-600 bg-slate-700/90 px-4 py-2 text-sm font-semibold text-slate-100 transition hover:bg-slate-600"
                >
                  {t('profile.unfollow', 'Unfollow')}
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={handleFollowToggle}
                className="mt-3 w-full rounded-xl bg-sky-600/90 px-4 py-2 text-sm font-semibold text-white transition hover:bg-sky-500"
              >
                {t('profile.follow', 'Follow')}
              </button>
            )}
          </div>
        )}

        {message && (
          <div className="bg-green-900/50 border border-green-700 text-green-200 p-3 rounded-lg mb-4 text-sm font-medium">
            {message}
          </div>
        )}

       <div className="flex flex-col items-center mt-6 mb-8">
         <p className="font-semibold mb-4 text-gray-300">{t('profile.photo', 'Profile picture')}</p>
         {isOwnProfile ? (
           <>
             <ImageUploadField
               label=""
               shape="circle"
               value={photoUrl}
               currentPath={photoPath}
               storagePathPrefix="profilePhotos"
               onChange={handlePhotoChange}
               helperText={t('profile.photoHelp', 'JPG, PNG, WEBP or GIF, up to 5MB.')}
             />
             {/* Custom avatar is optional and only ever shown when no photo
                 is uploaded — an uploaded photo always takes priority
                 (see Avatar.js). */}
             <div className="mt-4 w-full">
               <button
                 type="button"
                 onClick={() => setShowAvatarEditor((v) => !v)}
                 className="mx-auto flex items-center gap-2 rounded-xl border border-slate-600 bg-slate-700/80 px-4 py-2 text-xs font-semibold text-slate-100 transition hover:bg-slate-600"
               >
                 <Avatar avatarConfig={avatarConfig} size={24} />
                 {showAvatarEditor
                   ? t('avatar.hideEditor', 'Hide avatar editor')
                   : t('avatar.customize', 'Customize your PartyBuddy avatar')}
               </button>
               {!photoUrl && (
                 <p className="mt-2 text-center text-[11px] text-slate-400">
                   {t('avatar.usedWhenNoPhoto', 'Shown whenever you have no uploaded photo.')}
                 </p>
               )}
               {showAvatarEditor && (
                 <div className="mt-3">
                   <AvatarEditor value={avatarConfig} onChange={setAvatarConfig} />
                 </div>
               )}
             </div>
           </>
         ) : (
           <Avatar src={photoUrl} avatarConfig={avatarConfig} size={128} alt={profile.username || t("profile.defaultTitle", "Profile")} className="mb-4" />
         )}
         {photoError && <p className="mt-2 text-xs text-red-400">{photoError}</p>}

         <p className="font-semibold mt-8 mb-4 text-gray-300">{t('profile.qrTitle', 'QR code for this profile')}</p>

         {canViewQrCode(currentUser, profile) ? (
           <div className="relative rounded-2xl border border-slate-700 bg-slate-900/80 p-4">
             <QRCodeSVG value={profile.id} size={128} />
           </div>
         ) : (
           <div className="relative rounded-2xl border border-slate-700 bg-slate-900/80 p-4 text-center text-sm text-slate-200">
             <div className="blur-sm pointer-events-none">
               <QRCodeSVG value={profile.id} size={128} />
             </div>
             <div className="absolute inset-0 flex items-center justify-center rounded-2xl bg-slate-900/90 px-4 text-center text-sm font-semibold text-slate-100">
               {t('profile.qrLocked', 'QR code visible only in person')}
             </div>
           </div>
         )}
        </div>

        <div className="space-y-4">
          <input
            type="text"
            placeholder={`🎵 ${t('profile.musicStyle', 'Favorite music style')}`}
            value={musicStyle}
            onChange={(e) => setMusicStyle(e.target.value)}
            className="w-full bg-slate-700 border border-slate-600 rounded-lg p-3 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-400 focus:border-transparent transition"
            disabled={!isOwnProfile}
          />

          <input
            type="text"
            placeholder={`🏙️ ${t('profile.city', 'City')}`}
            value={city}
            onChange={(e) => setCity(e.target.value)}
            className="w-full bg-slate-700 border border-slate-600 rounded-lg p-3 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-400 focus:border-transparent transition"
            disabled={!isOwnProfile}
          />

          {isOwnProfile && (
            <button
              type="submit"
              className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-3 rounded-lg font-semibold hover:from-blue-500 hover:to-indigo-500 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
            >
              💾 {t('profile.save', 'Save')}
            </button>
          )}
        </div>
      </form>
    </div>
  );
}

export default Profile;